import clsx from "clsx";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { Controller, useWatch } from "react-hook-form";
import { reactSelectCustomStyles } from "../../../../Employees/Employee/Information";

type AccessControlProvider = "shelly" | "tuya";

const AccessControlCheckSection = ({
  provider,
  type,
  inputsDisabled,
  enabled,
}: {
  provider: AccessControlProvider;
  type: "checkin" | "checkout";
  inputsDisabled: boolean;
  enabled: boolean;
}) => {
  const { t } = useTranslation(["screens"]);

  const memberTypes = [
    "employee",
    "visitor",
    "guest",
    "meetingAttendee",
  ] as const;

  return (
    <div>
      <Controller
        name={`${provider}.${type}.enabled`}
        render={({ field: { value = false, name, onChange, ...rest } }) => (
          <div className="form-check my-2">
            <input
              id={name}
              {...rest}
              type="checkbox"
              className="form-check-input"
              checked={value === true}
              onChange={() => onChange(!value)}
              disabled={inputsDisabled}
            />
            <label className="form-check-label" htmlFor={name}>
              {t(`screens:screen.settings.shelly.${type}.enabled`)}
            </label>
          </div>
        )}
      />
      {enabled && (
        <>
          <Controller
            name={`${provider}.${type}.autoSwitch`}
            render={({ field: { value = false, name, onChange, ...rest } }) => (
              <div className="form-check ml-3 mb-2">
                <input
                  id={name}
                  {...rest}
                  type="checkbox"
                  className="form-check-input"
                  checked={value === true}
                  onChange={() => onChange(!value)}
                  disabled={inputsDisabled}
                />
                <label className="form-check-label" htmlFor={name}>
                  {t(`screens:screen.settings.shelly.${type}.autoSwitch`)}
                </label>
              </div>
            )}
          />
          {memberTypes.map((memberType) => (
            <Controller
              key={memberType}
              name={`${provider}.${type}.${memberType}.enabled`}
              render={({
                field: { value = false, name, onChange, ...rest },
              }) => (
                <div className="form-check ml-4">
                  <input
                    id={name}
                    {...rest}
                    type="checkbox"
                    className="form-check-input"
                    checked={value === true}
                    onChange={() => onChange(!value)}
                    disabled={inputsDisabled}
                  />
                  <label className="form-check-label" htmlFor={name}>
                    {t(
                      `screens:screen.settings.shelly.${type}.${memberType}.enabled`,
                    )}
                  </label>
                </div>
              )}
            />
          ))}
        </>
      )}
    </div>
  );
};

const PROVIDER_CONFIG = {
  shelly: {
    titleKey: "screens:screen.settings.shelly",
    getLabel: (c: IShellyController) => c.title,
    filter: (c: IShellyController) => c.role === "DOOR",
  },
  tuya: {
    titleKey: "screens:screen.settings.tuya",
    getLabel: (c: ITuyaController) => c.deviceName,
    filter: () => true,
  },
} as const;

const AccessControlSettings = ({
  provider,
  disabled,
  controllersData,
}: {
  provider: "shelly" | "tuya";
  disabled: boolean;
  controllersData?: {
    controllers: (IShellyController | ITuyaController)[];
  };
}) => {
  const { t } = useTranslation(["screens"]);

  const enabled = useWatch({ name: `${provider}.enabled` });
  const checkinEnabled = useWatch({ name: `${provider}.checkin.enabled` });
  const checkoutEnabled = useWatch({ name: `${provider}.checkout.enabled` });

  return (
    <div className="form-group mb-4">
      <h6>{t(`${PROVIDER_CONFIG[provider].titleKey}.title`)}</h6>
      <Controller
        name={`${provider}.enabled`}
        render={({ field: { value = false, name, onChange, ...rest } }) => (
          <div className="form-check my-2">
            <input
              id={name}
              {...rest}
              type="checkbox"
              className="form-check-input"
              disabled={disabled}
              checked={value === true}
              onChange={() => onChange(!value)}
            />
            <label className="form-check-label" htmlFor={name}>
              {t("screens:screen.settings.shelly.enabled")}
            </label>
          </div>
        )}
      />
      {enabled && (
        <div className="ml-3">
          <Controller
            name={`${provider}.controller`}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <div className="form-group mb-2">
                <label>{t(`screens:screen.settings.shelly.controller`)}</label>

                <Select
                  getOptionLabel={PROVIDER_CONFIG[provider].getLabel as any}
                  getOptionValue={(c: any) => c.id}
                  options={
                    controllersData?.controllers.filter(
                      PROVIDER_CONFIG[provider].filter as any,
                    ) || []
                  }
                  isClearable
                  value={
                    controllersData?.controllers.find((c) => c.id === value) ||
                    null
                  }
                  onChange={(opt) => onChange(opt?.id || null)}
                  className={clsx({ "is-invalid": !!error })}
                  styles={reactSelectCustomStyles(!!error)}
                />

                <div className="invalid-feedback">{error?.message}</div>
              </div>
            )}
          />
          <AccessControlCheckSection
            provider={provider}
            type="checkin"
            inputsDisabled={disabled}
            enabled={checkinEnabled}
          />

          <AccessControlCheckSection
            provider={provider}
            type="checkout"
            inputsDisabled={disabled}
            enabled={checkoutEnabled}
          />
        </div>
      )}
    </div>
  );
};

export default AccessControlSettings;
